import React, { Component } from "react";
import { times, uniq } from "lodash";
import classnames from "classnames";
import runes from "../constants/runes";

const dotDiameter = 15;
const padSideLength = 120;
const padPadding = 10;

function generateDotPositions(sideLength, dotDiameter) {
  const positions = times(
    3,
    i => padPadding / 2 + i * (sideLength / 2 - dotDiameter / 2)
  );
  const final = [];
  times(3, i => {
    times(3, j => {
      final.push([positions[j], positions[i]]);
    });
  });
  return final;
}

function cycleToLowestElement(items) {
  const smallestElement = [...items].sort((a, b) => a - b)[0];
  const serialized = items.join("");
  const smallestElementIndex = serialized.indexOf(smallestElement.toString());
  return (
    serialized.slice(smallestElementIndex) +
    serialized.slice(0, smallestElementIndex)
  );
}

const positions = generateDotPositions(padSideLength, dotDiameter);

class RunePad extends Component {
  static defaultProps = {
    loadTimeOffset: 5,
    brushSize: 3,
    brushColor: "#444",
    disabled: false
  };

  constructor(props) {
    super(props);

    this.state = { activeDot: undefined, activatedDots: [], hasRune: false };

    this.isMouseDown = false;
    this.startDrawIdx = [];
    this.timeoutValidity = 0;
    this.currentRune = undefined;
  }

  componentDidMount() {
    if (!this.ctx) return;
    this.ctx.scale(2, 2);

    // https://github.com/facebook/react/issues/9809#issuecomment-413978405
    this.canvas.onmousedown = this.drawStart;
    this.canvas.onmouseup = this.drawEnd;
    this.canvas.onmouseout = this.drawEnd;
    this.canvas.onmousemove = this.draw;
    this.canvas.ontouchstart = this.drawStart;
    this.canvas.ontouchmove = this.draw;
    this.canvas.ontouchend = this.drawEnd;
    this.canvas.ontouchcancel = this.drawEnd;
  }

  getMousePos = e => {
    const rect = this.canvas.getBoundingClientRect();

    // use cursor pos as default
    let clientX = e.clientX;
    let clientY = e.clientY;

    // use first touch if available
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    // return mouse/touch position inside canvas
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  clear = () => {
    if (this.ctx) {
      this.ctx.clearRect(
        0,
        0,
        padSideLength + padPadding,
        padSideLength + padPadding
      );
    }
    this.timeoutValidity++;
    this.linesArray = [];
    this.startDrawIdx = [];
  };

  drawLine = line => {
    if (!this.ctx) return;

    this.ctx.strokeStyle = line.color;
    this.ctx.lineWidth = line.size;
    this.ctx.lineCap = "round";
    this.ctx.beginPath();
    this.ctx.moveTo(line.startX + 0.5, line.startY + 0.5);
    this.ctx.lineTo(line.endX + 0.5, line.endY + 0.5);
    this.ctx.stroke();
  };

  drawStart = e => {
    e.preventDefault();
    this.isMouseDown = true;

    const { x, y } = this.getMousePos(e);
    this.x = x;
    this.y = y;

    // make sure we start painting, useful to draw simple dots
    this.draw(e);
  };

  drawEnd = e => {
    e.preventDefault();
    this.isMouseDown = false;
    this.setState({ activeDot: undefined, activatedDots: [] });
    if (!this.state.hasRune) {
      this.clear();
    }
  };

  findCollision = (x, y) => {
    let index = 0;
    for (let [dotX, dotY] of positions) {
      index++;
      if (
        Math.abs(dotX - x + padPadding) <= dotDiameter &&
        Math.abs(dotY - y + padPadding) <= dotDiameter
      )
        return index;
    }
  };

  getDotCenter = dotNum => {
    const [x, y] = positions[dotNum - 1];
    return { x: x + dotDiameter / 2, y: y + dotDiameter / 2 };
  };

  getCurrentRune = activatedDots => {
    if (activatedDots.length === 0) return;
    const uniqueActivatedDots = uniq(activatedDots);
    const isOpen = uniqueActivatedDots.length - activatedDots.length === 0;
    if (isOpen) {
      return (
        runes.open[activatedDots.join("")] ||
        runes.open[[...activatedDots].reverse().join("")]
      );
    } else {
      return (
        runes.closed[cycleToLowestElement(uniqueActivatedDots)] ||
        runes.closed[cycleToLowestElement([...uniqueActivatedDots].reverse())]
      );
    }
  };

  draw = e => {
    //e.preventDefault();
    if (!this.isMouseDown || this.props.disabled) return;

    // calculate the current x, y coords
    const { x, y } = this.getMousePos(e);

    // Offset by 1 to ensure drawing a dot on click
    const newX = x + 1;
    const newY = y + 1;

    const activeDot = this.findCollision(newX, newY);
    let activatedDots = this.state.activatedDots;

    if (activatedDots.length === 0 && !activeDot) return;

    if (activeDot && activeDot !== activatedDots[activatedDots.length - 1]) {
      activatedDots.push(activeDot);
    }

    const currentRune = this.getCurrentRune(activatedDots);
    const hasRune =
      !!currentRune &&
      (!this.props.acceptedRunes ||
        this.props.acceptedRunes.indexOf(currentRune) !== -1);
    if (hasRune && currentRune !== this.currentRune) {
      this.props.onFoundRune && this.props.onFoundRune(currentRune);
      this.currentRune = currentRune;
    }

    const dotCenter = this.getDotCenter(
      activatedDots[activatedDots.length - 1]
    );
    // create current line object
    const line = {
      color: "#ddd",
      size: this.props.brushSize,
      startX: dotCenter.x,
      startY: dotCenter.y,
      endX: newX,
      endY: newY
    };

    this.clear();

    if (!hasRune) {
      this.drawLine(line);
    }

    for (let i = 0; i < activatedDots.length - 1; i++) {
      const fromDot = this.getDotCenter(activatedDots[i]);
      const toDot = this.getDotCenter(activatedDots[i + 1]);

      this.drawLine({
        color: hasRune ? "#fff" : "#bbb",
        size: this.props.brushSize,
        startX: fromDot.x,
        startY: fromDot.y,
        endX: toDot.x,
        endY: toDot.y
      });
    }

    this.setState({ activeDot, activatedDots, hasRune });

    // set current x, y coords
    this.x = newX;
    this.y = newY;
  };

  stopScroll = e => {
    e.preventDefault();
  };

  render() {
    return (
      <div
        className="rune-pad"
        style={{
          width: padSideLength + padPadding,
          height: padSideLength + padPadding
        }}
      >
        <div
          className="pad-container"
          style={{
            width: padSideLength,
            height: padSideLength
          }}
        >
          {times(3, i => (
            <div key={i} className="pad-row">
              {times(3, j => {
                const dotNum = i * 3 + j + 1;
                return (
                  <div
                    key={j}
                    style={{
                      width: dotDiameter,
                      height: dotDiameter,
                      borderRadius: dotDiameter
                    }}
                    className={classnames(
                      "dot",
                      dotNum === this.state.activeDot && "dot-active",
                      this.state.activatedDots.indexOf(dotNum) !== -1 &&
                        "dot-activated",
                      this.state.hasRune && "dot-hidden"
                    )}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <canvas
          className="draw-canvas"
          width={(padSideLength + padPadding) * 2}
          height={(padSideLength + padPadding) * 2}
          style={{
            display: "block",
            width: padSideLength + padPadding,
            height: padSideLength + padPadding
          }}
          ref={canvas => {
            if (canvas) {
              this.canvas = canvas;
              this.ctx = canvas.getContext("2d");
            }
          }}
        />
      </div>
    );
  }
}

export default RunePad;



// WEBPACK FOOTER //
// ./src/components/RunePad.js